import React, { useState } from 'react';
import styled from 'styled-components';
import useAsyncEffect from 'use-async-effect';
import ReactECharts from 'echarts-for-react';
import moment from 'moment';
import api from '../../api/api';
import { to } from '../../util/util';
import { history } from '../../index';
import { HolderDetailsResult } from '../../model/HolderDetailsResult';

interface IProps {
    ticker: string;
    exchange: string;
    combinedTicker: string;
    holderId?: string;
    holderNumber?: string;
    name?: string;
    holding?: string;
}

const Wrapper = styled.div`
    .holder-details__back {
        cursor: pointer;
        color: #0b5cad;
        margin: 0.5rem 0;
        display: inline-block;
    }
    .badge {
        color: #fff;
        font-size: 0.65em;
        padding: 2px 8px;
        border-radius: 3px;
        margin-left: 0.75rem;
        vertical-align: middle;
    }
    .badge--new { background: #3498db; }
    .badge--subscribed { background: #27ae60; }
    .badge--unsubscribed { background: #c0392b; }
    dd .badge { font-size: 0.8em; }
    .holder-details__welcome {
        margin-left: 0.75rem;
        vertical-align: middle;
    }
    .holder-details__cards {
        display: flex;
        gap: 1rem;
        flex-wrap: wrap;
        margin: 1rem 0;
    }
    .holder-details__card {
        background: #f6f9fc;
        border-radius: 4px;
        padding: 1rem;
        flex: 1;
        min-width: 260px;
    }
    .holder-details__card h4 {
        margin: 0 0 0.75rem;
    }
    .holder-details__card dl {
        margin: 0;
        display: grid;
        grid-template-columns: auto 1fr;
        gap: 0.35rem 1rem;
    }
    .holder-details__card dt {
        color: #555;
        white-space: nowrap;
    }
    .holder-details__card dd {
        margin: 0;
    }
    .profit { color: #1a7f37; }
    .loss { color: #c0392b; }
    table {
        width: 100%;
        border-collapse: collapse;
        margin-top: 1rem;
    }
    th, td {
        padding: 0.5rem 0.75rem;
        border-bottom: 1px solid #e3e8ee;
        text-align: left;
    }
    th {
        background: #f6f9fc;
        color: #333;
        font-weight: 600;
    }
    td.num, th.num {
        text-align: right;
        white-space: nowrap;
    }
`;

const fmt = (n: number | undefined, decimals: number = 0): string =>
    n === undefined || n === null ? '' : n.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals });

const fmtDate = (d: string): string => d ? moment(d).format('DD MMM YYYY') : '';

const HolderDetails: React.FC<IProps> = (props: IProps) => {

    const [result, setResult] = useState<HolderDetailsResult | null>(null);
    const [error, setError] = useState('');

    useAsyncEffect(async (isMounted: () => boolean) => {
        const params: {holderId?:string, holderNumber?:string, name?:string, holding?:string} = {};
        if (props.holderId) params.holderId = props.holderId;
        else if (props.holderNumber) params.holderNumber = props.holderNumber;
        else if (props.name) { params.name = props.name; if (props.holding) params.holding = props.holding; }
        const [err, data] = await to(api().getHolderDetails(props.ticker, props.exchange, params));
        if (!isMounted()) return;
        if (err || !data.success) {
            setError(err?.message || data?.message || 'Failed to fetch holder details');
        } else {
            setResult(data);
            // canonicalize the URL to the stable holderId so it can be bookmarked/shared
            if (!props.holderId && data.holder?.holderId) {
                window.history.replaceState({}, document.title,
                    `${window.location.pathname}?holderId=${data.holder.holderId}`);
            }
        }
    }, []);

    const backToList = () => {
        const base = window.location.pathname.split('/').slice(0, 4).join('/');
        history.push(`${base}/Top Shareholders`);
    };
    const back = <span className="holder-details__back" onClick={backToList}>&larr; Back to Top Holders</span>;

    if (error) return <Wrapper>{back}<p style={{ color: '#c0392b' }}>{error}</p></Wrapper>;
    if (!result) return <Wrapper>{back}<p>Loading holder details from Automic&hellip; (first load can take a minute while reports generate)</p></Wrapper>;

    const holder = result.holder!;
    const summary = result.summary!;
    const movements = result.movements || [];

    // step series of holdings over time, extended to today with the current balance
    const holdingsData = movements.map(m => [m.date, m.closingBalance]);
    if (holdingsData.length > 0) {
        holdingsData.push([moment().format('YYYY-MM-DD'), summary.currentBalance]);
    }
    // for new holders, only chart prices from 1 year before the first investment so recent activity is legible
    const firstInvested = holder.firstInvestmentDate || movements[0]?.date;
    const priceFrom = result.isNew && firstInvested ? moment(firstInvested).subtract(1, 'year') : null;
    const priceData = (result.priceSeries || [])
        .filter(p => !priceFrom || moment(p.date).isSameOrAfter(priceFrom))
        .map(p => [p.date, p.close]);

    const chartOption = {
        tooltip: { trigger: 'axis' },
        legend: { data: ['Holding', 'Share Price'] },
        xAxis: { type: 'time' },
        yAxis: [
            { type: 'value', name: 'Holding', axisLabel: { formatter: (v: number) => v.toLocaleString() } },
            { type: 'value', name: 'Share Price', position: 'right', splitLine: { show: false } },
        ],
        series: [
            { name: 'Holding', type: 'line', step: 'end', symbol: 'none', data: holdingsData, yAxisIndex: 0 },
            { name: 'Share Price', type: 'line', symbol: 'none', data: priceData, yAxisIndex: 1 },
        ],
    };

    const profitClass = summary.profit >= 0 ? 'profit' : 'loss';

    return <Wrapper>
        {back}
        <h2>{holder.name}{result.isNew && <span className="badge badge--new">NEW</span>}</h2>
        <div className="holder-details__cards">
            <div className="holder-details__card">
                <h4>Holder Details</h4>
                <dl>
                    <dt>Holder No.</dt><dd>{holder.holderNumber}</dd>
                    <dt>Holder Id</dt><dd>{holder.holderId}</dd>
                    <dt>Type</dt><dd>{holder.holderType}</dd>
                    <dt>Address</dt><dd>{holder.address}</dd>
                    <dt>Email</dt><dd>{holder.email}
                        {holder.email && (result.unsubscribed
                            ? <span className="badge badge--unsubscribed">Unsubscribed</span>
                            : result.subscribedOn
                                ? <span className="badge badge--subscribed">Subscribed on {moment(result.subscribedOn).format('DD-MMM-YYYY')}</span>
                                : <button className="button-sm holder-details__welcome">Send Welcome Email</button>)}
                    </dd>
                    <dt>Phone</dt><dd>{holder.phone}</dd>
                    <dt>First Invested</dt><dd>{fmtDate(holder.firstInvestmentDate)}</dd>
                </dl>
            </div>
            <div className="holder-details__card">
                <h4>Position</h4>
                <dl>
                    <dt>Current Holding</dt><dd>{fmt(summary.currentBalance)}</dd>
                    <dt>Current Price</dt><dd>${fmt(summary.currentPrice, 3)}</dd>
                    <dt>Current Value</dt><dd>${fmt(summary.currentValue, 2)}</dd>
                    <dt>Net Invested</dt><dd>${fmt(summary.netInvested, 2)}</dd>
                    <dt>Profit / Loss</dt><dd className={profitClass}>${fmt(summary.profit, 2)}</dd>
                </dl>
            </div>
        </div>

        <ReactECharts option={chartOption} style={{ height: 400 }} notMerge={true} />

        <h3>Movement History</h3>
        <table>
            <thead>
                <tr>
                    <th>Date</th>
                    <th>Description</th>
                    <th>Broker</th>
                    <th className="num">Movement</th>
                    <th className="num">Price</th>
                    <th className="num">Closing Balance</th>
                </tr>
            </thead>
            <tbody>
                {movements.map((m, i) =>
                    <tr key={i}>
                        <td>{fmtDate(m.date)}</td>
                        <td>{m.description}</td>
                        <td>{m.broker}</td>
                        <td className={"num " + (m.movement >= 0 ? 'profit' : 'loss')}>{fmt(m.movement)}</td>
                        <td className="num">{m.price != null ? `$${fmt(m.price, 3)}${m.priceEstimated ? '*' : ''}` : (m.priceMissing ? '†' : '')}</td>
                        <td className="num">{fmt(m.closingBalance)}</td>
                    </tr>
                )}
                {movements.length === 0 && <tr><td colSpan={6}>No movements found</td></tr>}
            </tbody>
        </table>
        <p style={{fontSize:'0.8rem', fontStyle:'italic'}}>* the registry does not provide a transaction price for this movement — estimated from the closing price on the trade date</p>
        {movements.some(m => m.priceMissing) && <p style={{fontSize:'0.8rem', fontStyle:'italic'}}>† no closing price found near the trade date — this movement is excluded from Profit / Loss</p>}
    </Wrapper>
}

export default HolderDetails;
